/**
 * impersonation-session.ts — the client-side half of the consent-gated
 * impersonation session (ADR-081 Stage 4).
 *
 * The server mints a 30-min HMAC token (POST /portal/impersonation/mint); this
 * module holds it in sessionStorage and portal-api.ts attaches it to every call
 * as the `x-nexa-impersonation` header alongside the operator's own JWT. The
 * server re-verifies the grant per request, so a client revocation ends the
 * session on its next call — the fatal-code handler here just cleans up the
 * local state and returns the operator to their portal.
 *
 * sessionStorage (not localStorage): a client-mode session never survives the
 * tab, mirroring the teardown-on-switch rule (§4.1).
 */

export interface MniSession {
  token: string;
  clientAccountId: string;
  clientName: string | null;
  operatorType: 'partner' | 'beta' | 'consultant';
  operatorOrgId: string;
  expiresAt: string; // ISO
}

const KEY = 'nexa_mni_session';

/** Server error codes that END the session (revoked / expired / invalid). */
export const SESSION_FATAL_CODES: ReadonlySet<string> = new Set([
  'GRANT_REVOKED', 'SESSION_EXPIRED', 'INVALID_SESSION', 'SESSION_OPERATOR_MISMATCH',
]);

/** The operator surface to return to when a session ends. */
export const PORTAL_HOME: Record<MniSession['operatorType'], string> = {
  partner: '/partner',
  beta: '/portal/beta',
  consultant: '/portal/consultant',
};

/** Operator portal display names — match the L1 chooser / operator portal
 *  titles. Shared by the purple-bar exit pill (ImpersonationBanner) and the
 *  switch-context blocked modal, which quotes that pill's label verbatim. */
export const PORTAL_NAMES: Record<MniSession['operatorType'], string> = {
  partner: 'Partner',
  beta: 'Beta Management',
  consultant: 'Nexa Consultant',
};

export function getMniSession(): MniSession | null {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    const s = JSON.parse(raw) as MniSession;
    if (!s.token || !s.clientAccountId) return null;
    // Locally expired → clean up now rather than bouncing off the server.
    if (s.expiresAt && new Date(s.expiresAt).getTime() <= Date.now()) {
      sessionStorage.removeItem(KEY);
      return null;
    }
    return s;
  } catch {
    return null;
  }
}

export function setMniSession(s: MniSession): void {
  try { sessionStorage.setItem(KEY, JSON.stringify(s)); } catch { /* private mode */ }
}

export function clearMniSession(): void {
  try { sessionStorage.removeItem(KEY); } catch { /* private mode */ }
}

/** End the session locally and return the operator to their portal home. */
export function exitMniSession(session?: MniSession | null): void {
  const s = session ?? getMniSession();
  clearMniSession();
  window.location.assign(s ? PORTAL_HOME[s.operatorType] ?? '/' : '/');
}
